import { useCallback } from 'react';
import { useAuthtoken } from 'stores/useAuthStore';
import { useMutation } from '@tanstack/react-query';

import request from 'utils/request';
import hostingPath from 'utils/hostingPath';
import { queryClient } from 'providers/QueryProvider';
import { isNativeDocType } from '@/constants/nativeDocTypes';
import { setAppLoading, setWebDialog, showSnackbar } from 'stores/useUiStore';

import type { AddDocumentPayload } from '../docs.types';
import { DOCS_LIST_QUERY_KEY } from './useDocsList';
import { useWebDialogDocStore, type WebDialogDoc } from './useWebDialogDocStore';

type DocsPostResponse = { success: boolean; message?: string };
type DocsGetResponse = {
  success: boolean;
  message: Array<{ page_id: number; pageTitle?: string } | null>;
};
type DocLinksResponse = {
  success: boolean;
  data: Array<{ docID: number; pageTitle?: string }>;
};

const slugify = (docName: string) => docName?.replaceAll(' ', '_');

function useDocsHeaders() {
  const authtoken = useAuthtoken();
  return {
    authtoken,
    headers: {
      'Content-Type': 'application/json',
      authorization: authtoken,
    },
  };
}

export function useAddDoc() {
  const { authtoken, headers } = useDocsHeaders();
  const setActiveDoc = useWebDialogDocStore((s) => s.setActiveDoc);

  return useMutation<DocsPostResponse, Error, AddDocumentPayload>({
    mutationFn: async (doc) => {
      const result = await request<DocsPostResponse>('/api/docs', {
        method: 'POST',
        body: JSON.stringify(doc),
        headers,
      });
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to create document');
      }
      return result;
    },
    onSuccess: (_result, doc) => {
      if (!['Instruction', 'Checksheet', 'Program'].includes(doc.docType)) {
        return;
      }
      const docTemplate = doc?.template?.pageTitle
        ? `?preload=${doc.template.pageTitle}`
        : '';
      setActiveDoc({ ...doc, openedDate: new Date().toISOString(), isNew: true });
      setWebDialog({
          show: true,
          url: hostingPath(
            'docs',
            `index.php/Special:FormEdit/${doc.docType}/${doc.docName}${docTemplate}`,
            authtoken
          ),
          label: `${doc.docName}`,
        });
    },
    onError: (error) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onDocAdd',
          open: true,
          message: error.message,
          severity: 'error',
        });
    },
  });
}

export function useEditDoc() {
  const { authtoken, headers } = useDocsHeaders();
  const setActiveDoc = useWebDialogDocStore((s) => s.setActiveDoc);

  return useMutation<DocsPostResponse, Error, WebDialogDoc>({
    mutationFn: async (doc) => {
      const result = await request<DocsPostResponse>('/api/docs', {
        method: 'POST',
        body: JSON.stringify(doc),
        headers,
      });
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to edit document');
      }
      return result;
    },
    onSuccess: (_result, doc) => {
      const docName = slugify(doc.docName);
      if (doc.pageNamespace === 6) {
        setActiveDoc({ ...doc, openedDate: new Date().toISOString() });
        setWebDialog({
            show: true,
            url: hostingPath(
              'docs',
              `index.php?title=Special:Upload&wpDestFile=${docName}&wpForReUpload=1`,
              authtoken
            ),
            label: `${doc.docName}`,
          });
        return;
      }

      let url = hostingPath(
        'docs',
        `index.php?title=${docName}&action=formedit`,
        authtoken
      );
      if (doc.revId) {
        url = hostingPath(
          'docs',
          `index.php?title=${docName}&oldid=${doc.revId}&action=formedit`,
          authtoken
        );
      }

      let openWiki = true;
      if (doc.docType === 'Program') {
        url = hostingPath(
          'docs',
          `index.php/Special:FormEdit/${doc.docType}/${docName}`,
          authtoken
        );
      } else if (doc.docType === 'Instruction') {
        url = hostingPath(
          'docs',
          `index.php/Special:FormEdit/Instruction/${docName}`,
          authtoken
        );
      } else if (isNativeDocType(doc.docType)) {
        openWiki = false;
        showSnackbar({
            open: true,
            caller: 'onDocEdit',
            message:
              'Part Alert saved. Edit the body in Document Manager (select the part, then the document).',
            severity: 'info',
            autoHideDuration: 10000,
          });
      }

      if (openWiki) {
        setActiveDoc({ ...doc, openedDate: new Date().toISOString() });
        setWebDialog({ show: true, url, label: `${doc.docName}` });
      }
    },
    onError: (error) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onDocEdit',
          open: true,
          message: error.message,
          severity: 'error',
        });
    },
  });
}

export function useViewDoc() {
  const { authtoken, headers } = useDocsHeaders();
  const setActiveDoc = useWebDialogDocStore((s) => s.setActiveDoc);

  return useMutation<DocsPostResponse, Error, WebDialogDoc>({
    mutationFn: async (doc) => {
      const result = await request<DocsPostResponse>('/api/docs', {
        method: 'POST',
        body: JSON.stringify(doc),
        headers,
      });
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to view document');
      }
      return result;
    },
    onSuccess: async (_result, doc) => {
      const docName = slugify(doc.docName);
      const openedDate = new Date().toISOString();

      if (doc.pageNamespace === 6) {
        setActiveDoc({ ...doc, openedDate });
        setWebDialog({
            show: true,
            url: hostingPath('docs', `index.php/File:${docName}`, authtoken),
            label: `${doc.docName}`,
          });
        return;
      }

      try {
        const action = doc.edit ? '&action=formedit' : '';

        if (!isNativeDocType(doc.docType)) {
          const getDocResult = await request<DocsGetResponse>(
            `/api/docs?docName=${encodeURIComponent(docName)}`,
            { method: 'GET', headers }
          );
          await request('/api/docs/validateCategoryLink', {
            method: 'POST',
            body: JSON.stringify({
              pageId: getDocResult?.message?.[0]?.page_id,
              pageTitle: docName,
              docType: doc.docType,
            }),
            headers,
          });
        }

        if (isNativeDocType(doc.docType) && doc.edit) {
          showSnackbar({
              open: true,
              caller: 'onDocView',
              message:
                'Part Alerts are edited in Document Manager: open Production Data, select the part, then the Part Alert document.',
              severity: 'info',
              autoHideDuration: 12000,
            });
          return;
        }

        const url = doc.revId
          ? hostingPath(
              'docs',
              `index.php?title=${encodeURIComponent(docName)}&oldid=${
                doc.revId
              }${action}`,
              authtoken
            )
          : hostingPath(
              'docs',
              `index.php?title=${encodeURIComponent(docName)}${action}`,
              authtoken
            );
        setActiveDoc({ ...doc, openedDate });
        setWebDialog({ show: true, url, label: `${doc.docName}` });
      } catch (err) {

        console.log(err);
      }
    },
    onError: (error) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onDocView',
          open: true,
          message: error.message,
          severity: 'error',
        });
    },
  });
}

const deleteDialogPath = (doc: WebDialogDoc, revIdFirst: boolean) => {
  const docName = slugify(doc.docName);
  if (doc.pageNamespace === 6) {
    return `index.php?title=File:${docName}&action=delete`;
  }
  if (doc.revId) {
    return revIdFirst
      ? `index.php?title=${docName}&oldid=${doc.revId}&action=delete`
      : `index.php?title=${docName}&action=delete&oldid=${doc.revId}`;
  }
  return `index.php?title=${docName}&action=delete`;
};

export function useDeleteDoc() {
  const { authtoken, headers } = useDocsHeaders();
  const setActiveDoc = useWebDialogDocStore((s) => s.setActiveDoc);

  return useMutation<DocsPostResponse, Error, WebDialogDoc>({
    onMutate: () => {
      setAppLoading({ isLoading: true, label: 'Deleting Document' });
    },
    mutationFn: async (doc) => {
      const result = await request<DocsPostResponse>('/api/docs', {
        method: 'POST',
        body: JSON.stringify(doc),
        headers,
      });
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to delete document');
      }
      return result;
    },
    onSuccess: (_result, doc) => {
      setActiveDoc({ ...doc });
      setWebDialog({
          show: true,
          url: hostingPath('docs', deleteDialogPath(doc, true), authtoken),
          label: `${doc.docName}`,
        });
    },
    onError: (error) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onDocDelete',
          open: true,
          message: error.message,
          severity: 'error',
        });
    },
    onSettled: () => {
      setAppLoading({ isLoading: false, label: '' });
    },
  });
}

export function useDeleteDocWithLinks() {
  const { authtoken, headers } = useDocsHeaders();
  const setActiveDoc = useWebDialogDocStore((s) => s.setActiveDoc);

  return useMutation<DocsPostResponse, Error, WebDialogDoc>({
    onMutate: () => {
      setAppLoading({ isLoading: true, label: 'Deleting links and document' });
    },
    mutationFn: async (doc) => {
      const docLinksRes = await request<DocLinksResponse>(
        `/api/documents?pageID=${doc.pageId}`,
        { method: 'GET', headers }
      );

      if (docLinksRes?.success && docLinksRes.data.length > 0) {
        for (const link of docLinksRes.data) {
          // eslint-disable-next-line no-await-in-loop
          const linkDeleteRes = await request<DocsPostResponse>(
            `/api/documents/${link.docID}`,
            { method: 'DELETE', headers }
          );
          if (!linkDeleteRes?.success) {
            throw new Error(
              `Failed to delete link ${link.docID}: ${
                linkDeleteRes?.message || 'Unknown error'
              }`
            );
          }
        }
      }

      const result = await request<DocsPostResponse>('/api/docs', {
        method: 'POST',
        body: JSON.stringify(doc),
        headers,
      });
      if (!result?.success) {
        throw new Error(result?.message || 'Error deleting document and links');
      }
      return result;
    },
    onSuccess: (_result, doc) => {
      setActiveDoc({ ...doc });
      setWebDialog({
          show: true,
          url: hostingPath('docs', deleteDialogPath(doc, false), authtoken),
          label: `${doc.docName}`,
        });
    },
    onError: (error) => {
      showSnackbar({
          autoHideDuration: null,
          caller: 'onDocDeleteWithLinks',
          open: true,
          message: error.message || 'Error deleting document and links',
          severity: 'error',
        });
    },
    onSettled: () => {
      setAppLoading({ isLoading: false, label: '' });
    },
  });
}

export function useWebDialogClose() {
  const authtoken = useAuthtoken();
  const clearActiveDoc = useWebDialogDocStore((s) => s.clearActiveDoc);

  return useCallback(async () => {
    queryClient.invalidateQueries({ queryKey: DOCS_LIST_QUERY_KEY });

    const doc = useWebDialogDocStore.getState().activeDoc;
    if (!doc) return;

    const headers = {
      'Content-Type': 'application/json',
      authorization: authtoken,
    };
    try {
      const params = new URLSearchParams(
        doc.pageId ? { pageId: String(doc.pageId) } : { docName: doc.docName }
      );
      const result = await request<DocsGetResponse>(
        `/api/docs?${params.toString()}`,
        { method: 'GET', headers }
      );

      const found = result?.message[0];
      if (result?.success && found) {
        const docName = found.pageTitle;

        if (!isNativeDocType(doc.docType)) {
          await request('/api/docs/validateCategoryLink', {
            method: 'POST',
            body: JSON.stringify({
              pageId: found.page_id,
              pageTitle: docName,
              docType: doc.docType,
            }),
            headers,
          });
        }

        const docLinksRes = await request<DocLinksResponse>(
          `/api/documents?pageID=${found.page_id}`,
          { method: 'GET', headers }
        );
        if (docLinksRes?.success && docLinksRes.data.length > 0) {
          const idsNeedingUpdate = docLinksRes.data
            .filter((dl) => dl.pageTitle !== docName)
            .map((dl) => dl.docID);
          if (idsNeedingUpdate.length > 0) {
            await request('/api/documents', {
              method: 'PUT',
              body: JSON.stringify({
                ids: idsNeedingUpdate,
                property: 'pageTitle',
                value: docName,
              }),
              headers,
            });
          }
        }

        const pageId = doc.pageId || found.page_id;
        if (pageId) {
          await request('/api/docs/auto-approve', {
            method: 'POST',
            body: JSON.stringify({
              pageId,
              docType: doc.docType,
              isNew: !!doc.isNew,
              ...(doc.openedDate ? { dateLimit: doc.openedDate } : {}),
            }),
            headers,
          });
        }
      }

      clearActiveDoc();
    } catch (err) {

      console.log(err);
    }
  }, [authtoken, clearActiveDoc]);
}
